/**
 * Presentational component for a PMEI parameter cell's differences tooltip: it wraps the cell's
 * content in an explanatory tooltip describing how the cell's values differ from the plan. The
 * tooltip content is computed upstream by `computeDataParameterCellTooltip` (in
 * `PMEICellTooltipContent.ts`); this component holds no logic beyond presentation:
 * - a single parameter-level message (empty / unplanned / a lone difference) → a one-line tooltip;
 * - a multi-value differing cell → two headed sections ("N values differing from plan", then "All
 *   values"), each a bulleted list with one bullet per value (no truncation — the tooltip is the
 *   authoritative account of every value).
 *
 * The children are always wrapped in a full-height Box so that even an empty cell (which renders no
 * value) still presents a hover target for its "isn't confirmed yet" message.
 */
import {Box, createStyles, List, Stack, Text, Tooltip} from '@mantine/core';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';

import type {CellTooltipContent, TooltipLine, TooltipSection} from './PMEICellTooltipContent';

const TOOLTIP_LINE_HEIGHT = '14px';

export const EMPHASIS_FONT_WEIGHT = 700;

const useStyles = createStyles((theme) => ({
  tooltip: {
    maxWidth: 320,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    // Even padding on every side; the box grows to its content (up to maxWidth) and wraps.
    padding: 8,
    borderRadius: theme.radius.sm,
    fontSize: theme.fontSizes.xs,
    lineHeight: TOOLTIP_LINE_HEIGHT,
    // Mantine's Text/List set their own line-height, so force every descendant to inherit the
    // tooltip's instead.
    '& *': {
      lineHeight: TOOLTIP_LINE_HEIGHT,
    },
  },
  // Mantine's `withPadding` indents the list by spacing.xl (24px); the design wants a smaller,
  // marker-width inset (~1.5x the 11px list font) so bullets sit just inside the section heading.
  valueList: {
    paddingLeft: 16,
  },
}));

/** Renders one message line, bolding the segments a difference marks as emphasized. */
function MessageLine({line}: {line: TooltipLine}): JSX.Element {
  return (
    <>
      {line.segments.map((segment, i) => (
        <Text key={i} span={true} inherit={true} fw={segment.emphasis ? EMPHASIS_FONT_WEIGHT : undefined}>
          {segment.text}
        </Text>
      ))}
    </>
  );
}

/** A headed section — its title above a bulleted list, one bullet per value. */
function TooltipSectionBlock({section}: {section: TooltipSection}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Stack spacing={4}>
      <Text fz="xs" fw={600} component="div">
        {section.heading}
      </Text>
      <List fz="xs" spacing={2} classNames={{root: classes.valueList}}>
        {section.lines.map((line, i) => (
          <List.Item key={i}>
            <MessageLine line={line} />
          </List.Item>
        ))}
      </List>
    </Stack>
  );
}

function TooltipLabel({content}: {content: CellTooltipContent}): JSX.Element {
  if (content.kind === 'message') {
    return (
      <Text fz="xs" component="div">
        <MessageLine line={content.message} />
      </Text>
    );
  }
  return (
    <Stack spacing={8}>
      {content.sections.map((section, i) => (
        <TooltipSectionBlock key={i} section={section} />
      ))}
    </Stack>
  );
}

export default function PMEIParameterCellTooltip({
  tooltip,
  children,
}: {
  tooltip: CellTooltipContent;
  children: React.ReactNode;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Tooltip
      label={<TooltipLabel content={tooltip} />}
      classNames={{tooltip: classes.tooltip}}
      multiline={true}
      withinPortal={true}
      position="top"
    >
      <Box
        h="100%"
        // Floor the height so an empty cell (null content) still presents a hoverable target.
        mih={20}
        tabIndex={0}
        {...getTestProps({component: 'PMEIParameterCellTooltip', element: 'target'})}
      >
        {children}
      </Box>
    </Tooltip>
  );
}
